import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate} from "react-router-dom";
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Chip,
	CircularProgress,
	Collapse,
	MenuItem,
	Paper,
	Stack,
	TextField,
	Typography
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LoginIcon from "@mui/icons-material/Login";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import BusinessIcon from "@mui/icons-material/Business";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import ExtensionIcon from "@mui/icons-material/Extension";
import HubIcon from "@mui/icons-material/Hub";
import DnsIcon from "@mui/icons-material/Dns";
import {useTranslation} from "react-i18next";
import {TRACK_PATH} from "@constants/config";
import {PATH_ADMIN_ACCOUNTS} from "@constants/navigate";
import {
	AdminGeocoderMode,
	AdminGeocoderProvider,
	IAdminAccountDetails,
	IAdminAccountEditable,
	IAdminMultiAccountOption
} from "@containers/adminAccounts/types";
import {
	useConnectToAccountQuery,
	useGetAdminAccountQuery,
	useUpdateAdminAccountQuery
} from "@containers/adminAccounts/hooks/useAdminAccountsQuery";
import {accountInitials} from "@containers/adminAccounts/helpers/initials";
import AdminHero, {HeroButton} from "@containers/adminAccounts/ui/AdminHero";
import SectionCard from "@containers/adminAccounts/ui/SectionCard";
import SettingSwitch from "@containers/adminAccounts/ui/SettingSwitch";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type TextKey = {
	[K in keyof IAdminAccountEditable]: IAdminAccountEditable[K] extends string ? K : never
}[keyof IAdminAccountEditable]

const SERVERS: TextKey[][] = [
	['addressGeocodingServer', 'apiKeyGeocodingServer'],
	['addressRoutingServer', 'apiKeyRoutingServer'],
	['addressTransitPredictionServer', 'apiKeyTransitPredictionServer'],
]

const toEditable = (details: IAdminAccountDetails): IAdminAccountEditable => ({
	active: details.active,
	multiAccount: details.multiAccount,
	tenantAdmin: details.tenantAdmin,
	newPassword: '',
	geocoderMode: details.geocoderMode ?? 'GEOZONE',
	geocoderProvider: details.geocoderProvider ?? 'OSM',
	smsProperties: details.smsProperties ?? '',
	description: details.description ?? '',
	labelName: details.labelName ?? '',
	notes: details.notes ?? '',
	maximumDevices: details.maximumDevices ?? 0,
	contactName: details.contactName ?? '',
	contactEmail: details.contactEmail ?? '',
	contactPhone: details.contactPhone ?? '',
	visibleFuelInfo: details.visibleFuelInfo,
	visibleSingleVoyage: details.visibleSingleVoyage,
	visibleStaffSchedule: details.visibleStaffSchedule,
	addressGeocodingServer: details.addressGeocodingServer ?? '',
	apiKeyGeocodingServer: details.apiKeyGeocodingServer ?? '',
	addressRoutingServer: details.addressRoutingServer ?? '',
	apiKeyRoutingServer: details.apiKeyRoutingServer ?? '',
	addressTransitPredictionServer: details.addressTransitPredictionServer ?? '',
	apiKeyTransitPredictionServer: details.apiKeyTransitPredictionServer ?? '',
	multiAccountIds: [...(details.multiAccountIds ?? [])].sort((a, b) => a - b),
	shareDefaultGroup: details.shareDefaultGroup,
	shareToMultiAccountIds: [...(details.shareToMultiAccountIds ?? [])].sort((a, b) => a - b),
})

const GEOCODER_MODES: AdminGeocoderMode[] = ['GEOZONE', 'FULL']
const GEOCODER_PROVIDERS: AdminGeocoderProvider[] = ['BING', 'ESRI', 'GOOGLE', 'NOKIA', 'OSM']

const formatDate = (seconds: number | null) => seconds ? new Date(seconds * 1000).toLocaleString('uk-UA') : '—'

const MultiAccountsSelect = ({label, options, value, onChange}: {
	label: string,
	options: IAdminMultiAccountOption[],
	value: number[],
	onChange: (value: number[]) => void,
}) => (
	<Autocomplete
		multiple
		size={'small'}
		options={options}
		value={options.filter((option) => value.includes(option.id))}
		onChange={(_, selected) => onChange(selected.map((option) => option.id).sort((a, b) => a - b))}
		getOptionLabel={(option) => option.code}
		isOptionEqualToValue={(option, selected) => option.id === selected.id}
		renderOption={(props, option) => (
			<li {...props} key={option.id}>
				<Box>
					<Typography sx={{fontSize: 14, lineHeight: 1.4}}>{option.code}</Typography>
					{option.description && <Typography sx={{fontSize: 12, lineHeight: 1.4}} color={'text.secondary'}>
						{option.description}
					</Typography>}
				</Box>
			</li>
		)}
		renderTags={(selected, getTagProps) => selected.map((option, index) => (
			<Chip {...getTagProps({index})} key={option.id} size={'small'} color={'primary'} variant={'outlined'}
				  label={option.code}/>
		))}
		renderInput={(params) => <TextField {...params} label={label}/>}
	/>
)

const AdminAccountEdit = ({accountId}: { accountId: number }) => {

	const {t} = useTranslation()
	const navigate = useNavigate()

	const {data, isLoading, isError} = useGetAdminAccountQuery({id: accountId})
	const {mutate: connect, isPending: isConnecting} = useConnectToAccountQuery()

	const [initial, setInitial] = useState<IAdminAccountEditable | null>(null)
	const [form, setForm] = useState<IAdminAccountEditable | null>(null)
	const [limit, setLimit] = useState('0')

	useEffect(() => {
		if (data) {
			const editable = toEditable(data)
			setInitial(editable)
			setForm(editable)
			setLimit(String(editable.maximumDevices))
		}
	}, [data])

	const {mutate: save, isPending: isSaving} = useUpdateAdminAccountQuery({
		onSuccess: (details) => {
			const editable = toEditable(details)
			setInitial(editable)
			setForm(editable)
			setLimit(String(editable.maximumDevices))
		}
	})

	const limitValue = Number(limit)
	const limitError = limit.trim() === '' || !Number.isInteger(limitValue) || limitValue < 0
	const emailError = !!form?.contactEmail && !EMAIL.test(form.contactEmail.trim())

	const dirty = useMemo(() => {
		if (!form || !initial) return false
		return JSON.stringify({...form, maximumDevices: limitValue}) !== JSON.stringify(initial)
	}, [form, initial, limitValue])

	const set = <K extends keyof IAdminAccountEditable>(key: K, value: IAdminAccountEditable[K]) => {
		setForm((prev) => prev ? {...prev, [key]: value} : prev)
	}

	const onSave = () => {
		if (!form || limitError || emailError) return
		save({...form, id: accountId, maximumDevices: limitValue})
	}

	const onReset = () => {
		if (!initial) return
		setForm(initial)
		setLimit(String(initial.maximumDevices))
	}

	if (isLoading) {
		return <Stack alignItems={'center'} sx={{py: 10}}><CircularProgress/></Stack>
	}

	if (isError || !data || !form) {
		return (
			<Stack gap={2} alignItems={'flex-start'}>
				<Button startIcon={<ArrowBackIcon/>} onClick={() => navigate(PATH_ADMIN_ACCOUNTS)}>{t('adminAccounts.back')}</Button>
				<Alert severity={'warning'}>{t('adminAccounts.notFound')}</Alert>
			</Stack>
		)
	}


	return (
		<Stack
			gap={2}
			sx={{
				pb: 12,
				// The app theme shrinks field labels and capitalises buttons; this page reads at a normal size.
				'& .MuiInputLabel-root': {fontSize: 14},
				'& .MuiInputLabel-shrink': {fontSize: 13},
				'& .MuiInputBase-input, & .MuiAutocomplete-input': {fontSize: 14},
				'& .MuiFormHelperText-root': {fontSize: 12},
				'& .MuiButton-root': {textTransform: 'none', fontWeight: 600, fontSize: 14},
			}}
		>

			<AdminHero
				leading={accountInitials(data.code)}
				title={data.code}
				subtitle={data.description || undefined}
				statuses={[
					{label: t(form.active ? 'adminAccounts.stateYES' : 'adminAccounts.stateNO'), tone: form.active ? 'success' : 'neutral'},
					...(form.multiAccount ? [{label: t('adminAccounts.stateEXTENDED'), tone: 'info' as const}] : []),
					...(form.tenantAdmin ? [{label: t('adminAccounts.stateTENANT'), tone: 'tenant' as const}] : []),
				]}
				facts={[
					{label: 'ID', value: data.id},
					{label: t('adminAccounts.dateCreating'), value: formatDate(data.createdAt)},
					{label: t('adminAccounts.dateOfLastEntry'), value: formatDate(data.lastLoginAt)},
				]}
				actions={<>
					<HeroButton
						primary
						startIcon={<LoginIcon/>}
						disabled={!data.active || isConnecting}
						onClick={() => connect(data)}
					>
						{t('adminAccounts.connect')}
					</HeroButton>
					<HeroButton endIcon={<OpenInNewIcon/>} href={`${TRACK_PATH}/account/${data.id}/edit`}>
						{t('adminAccounts.openLegacy')}
					</HeroButton>
				</>}
			/>

			<Box sx={{display: 'grid', gap: 2, gridTemplateColumns: {xs: '1fr', lg: 'minmax(0, 3fr) minmax(0, 2fr)'}}}>
				<Stack gap={2}>
					<SectionCard title={t('adminAccounts.cardMain')} icon={<BusinessIcon/>}>
						<SettingSwitch
							label={t('adminAccounts.active')}
							hint={t('adminAccounts.activeHint')}
							checked={form.active}
							onChange={(checked) => set('active', checked)}
						/>
						<SettingSwitch
							label={t('adminAccounts.multiAccount')}
							hint={t('adminAccounts.multiAccountHint')}
							checked={form.multiAccount}
							onChange={(checked) => set('multiAccount', checked)}
						/>
						<SettingSwitch
							label={t('adminAccounts.tenantAdmin')}
							hint={data.tenantAdmin ? t('adminAccounts.tenantAdminSet') : t('adminAccounts.tenantAdminHint')}
							checked={form.tenantAdmin}
							// The legacy page creates the tenant once and never removes it.
							disabled={data.tenantAdmin}
							onChange={(checked) => set('tenantAdmin', checked)}
						/>
						<TextField
							size={'small'}
							label={t('adminAccounts.description')}
							value={form.description}
							onChange={(event) => set('description', event.target.value)}
							inputProps={{maxLength: 128}}
						/>
						<Box sx={{display: 'grid', gap: 2, gridTemplateColumns: {xs: '1fr', md: '2fr 1fr'}}}>
							<TextField
								size={'small'}
								label={t('adminAccounts.labelName')}
								value={form.labelName}
								onChange={(event) => set('labelName', event.target.value)}
								inputProps={{maxLength: 255}}
							/>
							<TextField
								size={'small'}
								type={'number'}
								label={t('adminAccounts.maximumDevicesShort')}
								value={limit}
								onChange={(event) => setLimit(event.target.value)}
								error={limitError}
								helperText={t('adminAccounts.unlimited')}
								inputProps={{min: 0}}
							/>
						</Box>
						<TextField
							size={'small'}
							label={t('adminAccounts.notes')}
							value={form.notes}
							onChange={(event) => set('notes', event.target.value)}
							multiline
							minRows={3}
						/>
					</SectionCard>

					<SectionCard title={t('adminAccounts.cardAdmin')} icon={<ManageAccountsIcon/>}>
						<Box sx={{display: 'grid', gap: 2, gridTemplateColumns: {xs: '1fr', md: '1fr 1fr 1fr'}}}>
							<TextField
								size={'small'}
								label={t('adminAccounts.contactName')}
								value={form.contactName}
								onChange={(event) => set('contactName', event.target.value)}
								inputProps={{maxLength: 64}}
							/>
							<TextField
								size={'small'}
								label={t('adminAccounts.contactPhone')}
								value={form.contactPhone}
								onChange={(event) => set('contactPhone', event.target.value)}
								inputProps={{maxLength: 32}}
							/>
							<TextField
								size={'small'}
								label={t('adminAccounts.contactEmail')}
								value={form.contactEmail}
								onChange={(event) => set('contactEmail', event.target.value)}
								error={emailError}
								helperText={emailError ? t('adminAccounts.invalidEmail') : ' '}
								inputProps={{maxLength: 128}}
							/>
						</Box>
						<TextField
							size={'small'}
							type={'password'}
							autoComplete={'new-password'}
							label={t('adminAccounts.adminPassword')}
							value={form.newPassword}
							onChange={(event) => set('newPassword', event.target.value)}
							helperText={`${t('adminAccounts.adminPasswordHint')}${data.passwordChangedAt
								? ` ${t('adminAccounts.passwordChangedAt', {date: formatDate(data.passwordChangedAt)})}` : ''}`}
							inputProps={{maxLength: 32}}
						/>
					</SectionCard>

					<SectionCard title={t('adminAccounts.cardServices')} icon={<DnsIcon/>}>
						<Box sx={{display: 'grid', gap: 2, gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}}}>
							<TextField
								select
								size={'small'}
								label={t('adminAccounts.geocoderMode')}
								value={form.geocoderMode}
								onChange={(event) => set('geocoderMode', event.target.value as AdminGeocoderMode)}
							>
								{GEOCODER_MODES.map((mode) => <MenuItem key={mode} value={mode}>
									{t(`adminAccounts.geocoderMode${mode}`)}
								</MenuItem>)}
							</TextField>
							<TextField
								select
								size={'small'}
								label={t('adminAccounts.geocoderProvider')}
								value={form.geocoderProvider}
								onChange={(event) => set('geocoderProvider', event.target.value as AdminGeocoderProvider)}
							>
								{GEOCODER_PROVIDERS.map((provider) => <MenuItem key={provider} value={provider}>
									{provider.charAt(0) + provider.slice(1).toLowerCase()}
								</MenuItem>)}
							</TextField>
						</Box>
						{SERVERS.map(([address, key]) => (
							<Box key={address} sx={{display: 'grid', gap: 2, gridTemplateColumns: {xs: '1fr', md: '3fr 2fr'}}}>
								<TextField
									size={'small'}
									label={t(`adminAccounts.${address}`)}
									value={form[address]}
									onChange={(event) => set(address, event.target.value)}
								/>
								<TextField
									size={'small'}
									label={t(`adminAccounts.${key}`)}
									value={form[key]}
									onChange={(event) => set(key, event.target.value)}
								/>
							</Box>
						))}
						<TextField
							size={'small'}
							label={t('adminAccounts.smsProperties')}
							value={form.smsProperties}
							onChange={(event) => set('smsProperties', event.target.value)}
							inputProps={{maxLength: 200}}
						/>
					</SectionCard>
				</Stack>

				<Stack gap={2}>
					<SectionCard title={t('adminAccounts.cardModules')} icon={<ExtensionIcon/>}>
						<Stack gap={0.5}>
							<SettingSwitch
								label={t('adminAccounts.visibleFuelInfo')}
								checked={form.visibleFuelInfo}
								onChange={(checked) => set('visibleFuelInfo', checked)}
							/>
							<SettingSwitch
								label={t('adminAccounts.visibleSingleVoyage')}
								checked={form.visibleSingleVoyage}
								onChange={(checked) => set('visibleSingleVoyage', checked)}
							/>
							<SettingSwitch
								label={t('adminAccounts.visibleStaffSchedule')}
								checked={form.visibleStaffSchedule}
								onChange={(checked) => set('visibleStaffSchedule', checked)}
							/>
						</Stack>
					</SectionCard>

					<SectionCard title={t('adminAccounts.cardMultiAccounts')} icon={<HubIcon/>}>
						<MultiAccountsSelect
							label={t('adminAccounts.multiAccountIds')}
							options={data.availableMultiAccounts}
							value={form.multiAccountIds}
							onChange={(value) => set('multiAccountIds', value)}
						/>
						{form.multiAccount
							? <Typography sx={{fontSize: 13, lineHeight: 1.45}} color={'text.secondary'}>
								{t('adminAccounts.multiAccountNoShare')}
							</Typography>
							: <>
								<SettingSwitch
									label={t('adminAccounts.shareDefaultGroup')}
									hint={t('adminAccounts.shareDefaultGroupHint')}
									checked={form.shareDefaultGroup}
									onChange={(checked) => set('shareDefaultGroup', checked)}
								/>
								<Collapse in={form.shareDefaultGroup} unmountOnExit>
									<MultiAccountsSelect
										label={t('adminAccounts.shareToMultiAccountIds')}
										options={data.availableMultiAccounts}
										value={form.shareToMultiAccountIds}
										onChange={(value) => set('shareToMultiAccountIds', value)}
									/>
								</Collapse>
							</>}
					</SectionCard>

					<Alert severity={'info'} variant={'outlined'} sx={{borderRadius: 3}}>
						{t('adminAccounts.legacyNote')}
					</Alert>
				</Stack>
			</Box>

			<Paper
				elevation={8}
				sx={(theme) => ({
					position: 'sticky',
					bottom: 16,
					zIndex: 5,
					borderRadius: 3,
					px: 2,
					py: 1.25,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: 2,
					borderLeft: `4px solid ${dirty ? theme.palette.warning.main : theme.palette.success.main}`,
					transition: 'border-color .2s',
				})}
			>
				<Typography sx={{fontSize: 14, fontWeight: 500}} color={dirty ? 'warning.main' : 'text.secondary'}>
					{dirty ? t('adminAccounts.unsaved') : t('adminAccounts.saved')}
				</Typography>
				<Stack direction={'row'} gap={1}>
					<Button onClick={onReset} disabled={!dirty || isSaving}>{t('adminAccounts.cancel')}</Button>
					<Button
						variant={'contained'}
						onClick={onSave}
						disabled={!dirty || isSaving || limitError || emailError}
						startIcon={isSaving ? <CircularProgress size={16} color={'inherit'}/> : undefined}
					>
						{t('adminAccounts.save')}
					</Button>
				</Stack>
			</Paper>
		</Stack>
	);
};

export default AdminAccountEdit;
